import {
  capturePwaRegistrationError,
  shouldRegisterServiceWorker,
  unregisterStaleServiceWorker,
} from './serviceWorkerPolicy';

// Structural copy of `RegisterSWOptions` from `virtual:pwa-register`. Declaring it here instead of
// importing the virtual module is what makes this file testable: Vitest never has to resolve a
// module that only exists while vite-plugin-pwa is running, so the caller injects the real
// `registerSW` and the test injects a spy.
export interface PanelServiceWorkerOptions {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegistered?: (registration: ServiceWorkerRegistration | undefined) => void;
  onRegisterError?: (error: Error) => void;
}

export type ServiceWorkerRegistrar = (options: PanelServiceWorkerOptions) => (reloadPage?: boolean) => Promise<void>;

// How often the panel re-checks for a newer worker once it is in the foreground again. Workbox only
// re-checks on a real NAVIGATION, and a mobile PWA resumes from background without one, so a
// rollout could stay invisible for hours; 5 min of throttle keeps a user flipping tabs from
// hammering the worker script.
const UPDATE_CHECK_THROTTLE_MS = 5 * 60 * 1000;
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

const watchForNewVersions = (registration: ServiceWorkerRegistration): void => {
  let lastUpdateCheck = 0;
  const checkForUpdate = () => {
    const now = Date.now();
    if (now - lastUpdateCheck < UPDATE_CHECK_THROTTLE_MS) return;
    lastUpdateCheck = now;
    registration.update().catch(() => {
      // A network failure while revalidating the worker must not affect the boot.
    });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  });
  setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
};

// Single entry point for the panel's service worker lifecycle. It used to live inline in
// `src/index.tsx`, which meant the branch that removes ~1,193 events/quarter of Electron noise
// (Sentry PLICK-FRONT-PANEL-COMERCIO-ZE) had no test around it: the entry module cannot be
// imported under Vitest. Here it can, and `registerPanelServiceWorker.spec.ts` asserts the wiring
// itself, not just the predicates it calls.
export const registerPanelServiceWorker = (registerSW: ServiceWorkerRegistrar): void => {
  if (!shouldRegisterServiceWorker()) {
    // Fire and forget: the cleanup never rejects (it guards itself) and must not delay the boot.
    unregisterStaleServiceWorker();
    return;
  }

  const updateServiceWorker = registerSW({
    onNeedRefresh() {
      // With `registerType: 'autoUpdate'` workbox normally applies the update by itself; this stays
      // explicit so that a worker left waiting is activated (skipWaiting + reload) instead of
      // letting the old one keep serving.
      updateServiceWorker(true).catch(() => undefined);
    },
    onRegistered(registration) {
      if (!registration) return;
      watchForNewVersions(registration);
    },
    onRegisterError: capturePwaRegistrationError,
  });
};
